import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  ChevronDown, ChevronUp, Eye, ArrowRight, Award, RotateCcw, Shuffle,
} from 'lucide-react';
import Modal from './Modal';
import { STEP_ORDER, STEP_LABELS, stepIndex, problemRepresentationStep } from '../utils/deriveSession';
import { splitSentences, freshSentences } from '../utils/sentenceDiff';
import { ui } from '../ui';

// Steps the facilitator reveals before advancing.
const REVEAL_STEPS = ['history', 'physical', 'differential', 'labs'];
const REVEAL_LABELS = {
  history: 'Reveal matches',
  physical: 'Reveal matches',
  differential: 'Reveal expert differential',
  labs: 'Reveal results',
};

/**
 * The facilitator's ONE contextual action for the current step, rendered by
 * Header next to the tab strip (which is also the progress stepper). It is the
 * only filled button on the screen, so it carries no colour of its own.
 *
 * Note the "next variant" label deliberately does NOT name the variant, the
 * name gives the diagnosis away, and this button is on the projector.
 */
export function caseAction({
  caseFlow, isFacilitator, spectator, hasNextStream,
  onRevealMatches, onAdvance, onRevealDiagnosis, onNextStream,
}) {
  if (!caseFlow.active || !isFacilitator || spectator) return null;
  const { step, completed, diagnosisRevealedAt, stream } = caseFlow;
  const idx = stepIndex(step);

  if (REVEAL_STEPS.includes(step) && !completed[step]) {
    return { label: REVEAL_LABELS[step], icon: Eye, onClick: () => onRevealMatches(step) };
  }
  if (step === 'cdm' && !diagnosisRevealedAt) {
    return { label: 'Reveal final diagnosis', icon: Award, onClick: onRevealDiagnosis };
  }
  if (step === 'cdm' && diagnosisRevealedAt && hasNextStream) {
    return {
      label: stream ? `Next variant (${stream.position + 1} of ${stream.total})` : 'Next variant',
      icon: Shuffle,
      onClick: onNextStream,
    };
  }
  if (step !== 'cdm') {
    return { label: `Advance to ${STEP_LABELS[STEP_ORDER[idx + 1]]}`, icon: ArrowRight, onClick: onAdvance };
  }
  return null;
}

// Scroll depth past which the case card compresses to a single line.
const COMPRESS_AT = 96;
// The highlight's full run (4 s hold + 800 ms fade), matching .pr-fresh.
const FRESH_MS = 4800;

// The statement as sentence spans, the fresh ones carrying the highlight. The
// animation starts at the time already elapsed, so a re-render (a scroll, a
// vote elsewhere) never restarts it.
function renderStatement(statement, fresh) {
  const live = fresh && fresh.text === statement && Date.now() - fresh.at < FRESH_MS ? fresh : null;
  if (!live) return statement;
  const elapsed = Date.now() - live.at;
  return splitSentences(statement).map((sentence, i) => (
    live.indices.has(i)
      ? <span key={i} className="pr-fresh" data-fresh="1" style={{ animationDelay: `-${elapsed}ms` }}>{sentence}</span>
      : <React.Fragment key={i}>{sentence}</React.Fragment>
  ));
}

/**
 * The single case card beneath the sticky tabs: title and chief complaint on
 * one line, the problem representation, and a text toggle for the full
 * findings list. Once the page scrolls the card compresses to one line (title
 * plus the statement's first sentence) with an expand affordance, so the step
 * board keeps the screen. Reset/End case live in the Facilitator menu.
 */
export default function CaseFlowPanel({
  caseFlow, historyBoard, physicalBoard, differentialSummary, isFacilitator, spectator,
  // Reset confirmation is controlled by App so the Facilitator menu can open it.
  confirmResetOpen, onCancelReset, onResetCase,
}) {
  // The full findings list sits behind a toggle, closed by default: the
  // problem representation is what the room should be reading.
  const [findingsOpen, setFindingsOpen] = useState(false);
  // A handover that wraps past five lines at the normal size drops the panel
  // font one step rather than being cut. Measured on the rendered paragraph,
  // so it holds at any width; re-measured when the statement or width changes.
  const statementRef = useRef(null);
  const [compact, setCompact] = useState(false);
  // Scroll compression, with a manual override that lasts until the page
  // returns to the top.
  const [scrolled, setScrolled] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);

  // The case restated after the latest revealed step; before any reveal the
  // demographics line stands in for it.
  const prStep = problemRepresentationStep(caseFlow);
  const statement = prStep ? caseFlow.problemRepresentation[prStep] : (caseFlow.demographics || '');
  const compressed = scrolled && !pinnedOpen;

  // What this reveal added: the sentences not in the previous step's
  // statement hold a tint and underline, then fade (see .pr-fresh). Seeded
  // with whatever is showing at mount, so a viewer who joins after a reveal
  // sees plain text, and skipped on the first reveal, which has no prior
  // statement to diff against. Fires once per rewrite.
  const prevRef = useRef({ step: prStep, statement });
  const [fresh, setFresh] = useState(null); // { text, indices: Set, at }
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { step: prStep, statement };
    if (!prStep || !prev.step || !statement || statement === prev.statement) return;
    const indices = freshSentences(prev.statement, statement);
    if (indices.length) setFresh({ text: statement, indices: new Set(indices), at: Date.now() });
  }, [statement, prStep]);

  useEffect(() => {
    const onScroll = () => {
      const deep = window.scrollY > COMPRESS_AT;
      setScrolled(deep);
      if (!deep) setPinnedOpen(false);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    const el = statementRef.current;
    if (!el) return undefined;
    const measure = () => {
      // Count lines at the normal size, then apply the decision to the element
      // itself as well as to state, so it holds even when state is unchanged.
      el.classList.remove('text-meta');
      el.classList.add('text-body');
      const lines = Math.round(el.getBoundingClientRect().height / parseFloat(getComputedStyle(el).lineHeight));
      const small = lines > 5;
      el.classList.toggle('text-body', !small);
      el.classList.toggle('text-meta', small);
      setCompact(small);
    };
    measure();
    // Re-measure on width changes only: the measurement itself changes the
    // height, which would otherwise re-trigger the observer every frame.
    let lastWidth = el.parentElement ? el.parentElement.clientWidth : -1;
    let frame = 0;
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      const width = el.parentElement ? el.parentElement.clientWidth : -1;
      if (width === lastWidth) return;
      lastWidth = width;
      // Deferred a frame: changing the font inside the observer's own delivery
      // trips "ResizeObserver loop completed with undelivered notifications".
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    }) : null;
    if (ro) ro.observe(el.parentElement || el);
    return () => { cancelAnimationFrame(frame); if (ro) ro.disconnect(); };
  }, [statement, compressed]);

  if (!caseFlow.active) {
    // The reset modal can still be open from the Facilitator menu.
    return null;
  }

  const { completed, diagnosisRevealedAt, stream } = caseFlow;

  // The running case summary, after each reveal the board shows every finding,
  // so the summary carries them all, not just the ones students elicited.
  const summarySections = [];
  if (completed.history && historyBoard.summaryDetails.length) {
    summarySections.push(['Hx', historyBoard.summaryDetails.join(' · ')]);
  }
  if (completed.physical && physicalBoard.summaryDetails.length) {
    summarySections.push(['Exam', physicalBoard.summaryDetails.join(' · ')]);
  }
  if (completed.differential && differentialSummary) {
    summarySections.push(['Ddx', differentialSummary]);
  }
  if (completed.labs && caseFlow.labs.length) {
    summarySections.push(['Labs', caseFlow.labs.map((l) => `${l.text}: ${l.detail}`).join(' · ')]);
  }

  const stepChip = prStep ? `after ${STEP_LABELS[prStep]}` : 'at presentation';
  const firstSentence = (statement.match(/^[^.!?]*[.!?]/) || [statement])[0].trim();

  return (
    <>
      <div className={`${ui.panel} mt-3`} data-testid="case-card" data-compressed={compressed ? '1' : '0'}>
        {compressed ? (
          <div className="px-3.5 py-2 flex items-center gap-3 min-w-0">
            <span className="text-body font-medium text-ink whitespace-nowrap">{caseFlow.title}</span>
            {firstSentence && (
              <span className="text-meta text-ink-2 truncate min-w-0">{firstSentence}</span>
            )}
            <button
              onClick={() => setPinnedOpen(true)}
              className={`${ui.btnText} h-7 px-2 ml-auto flex-shrink-0 text-meta text-ink-2`}
              aria-label="Expand case card"
              title="Show the full case card"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="px-3.5 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="text-heading font-medium text-ink">{caseFlow.title}</h2>
              <span className="text-body text-ink-2">{caseFlow.presentingSymptom}</span>
              {stream && stream.total > 1 && (
                /* Position only, the variant's LABEL names the diagnosis, so it
                   is kept in the Facilitator menu and never shown on the
                   projector. */
                <span className={ui.chip}>
                  <Shuffle className="w-3 h-3" />Variant {stream.position} of {stream.total}
                </span>
              )}
              {pinnedOpen && (
                <button
                  onClick={() => setPinnedOpen(false)}
                  className={`${ui.btnText} h-7 px-2 ml-auto text-meta text-ink-2`}
                  aria-label="Compress case card"
                  title="Back to one line"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              )}
            </div>

            {(statement || summarySections.length > 0) && (
              <div className="border-t border-line px-3.5 py-2.5" data-testid="problem-representation">
                <div className="flex items-center gap-2">
                  <span className={ui.label}>Problem representation</span>
                  <span className={ui.chip}>{stepChip}</span>
                </div>
                {statement && (
                  <p ref={statementRef} className={`mt-1 ${compact ? 'text-meta' : 'text-body'} text-ink`}>
                    {renderStatement(statement, fresh)}
                  </p>
                )}
                {summarySections.length > 0 && (
                  <>
                    <button
                      onClick={() => setFindingsOpen(!findingsOpen)}
                      className="mt-1.5 flex items-center gap-1 text-meta font-medium text-ink-2 hover:text-accent transition-colors"
                      aria-expanded={findingsOpen}
                    >
                      {findingsOpen ? 'Hide all findings' : 'Show all findings'}
                      {findingsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    {findingsOpen && (
                      <div className="mt-1.5 space-y-1 text-meta">
                        {caseFlow.demographics && (
                          <p className="text-ink"><span className="font-medium text-ink-2">Pt:</span> {caseFlow.demographics}</p>
                        )}
                        {summarySections.map(([label, text]) => (
                          <p key={label} className="text-ink">
                            <span className="font-medium text-ink-2">{label}:</span> {text}
                          </p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {!isFacilitator && !diagnosisRevealedAt && (
              <p className="border-t border-line px-3.5 py-1.5 text-meta text-ink-2">
                {completed[caseFlow.step]
                  ? 'Reviewing matches, waiting for the facilitator'
                  : `Working on ${STEP_LABELS[caseFlow.step]}`}
              </p>
            )}

            {diagnosisRevealedAt && caseFlow.diagnosis && (
              <div className="border-t border-line bg-accent-tint px-3.5 py-3 rounded-b-card">
                <p className="flex items-center gap-2 text-body font-semibold text-ink">
                  <Award className="w-4 h-4 text-accent" />Final diagnosis: {caseFlow.diagnosis.text}
                </p>
                {caseFlow.diagnosis.explanation && (
                  <p className="text-meta text-ink-2 mt-1">{caseFlow.diagnosis.explanation}</p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {confirmResetOpen && (
        <Modal title="Reset this case?" onClose={onCancelReset}>
          <p className="text-body text-ink-2">
            Restarts <span className="font-medium text-ink">{caseFlow.title}</span> at the History step
            for everyone in the session.
          </p>
          <ul className="text-meta text-ink-2 mt-3 space-y-1 list-disc pl-4">
            <li>Clears all student entries on the History, Physical and Labs boards</li>
            <li>Clears the differential, the CDM grid, all votes and the ranking timeline</li>
            <li>Keeps the case itself, findings, labs and the answer key are untouched</li>
          </ul>
          <p className="text-meta text-flag-red mt-3">
            This cannot be undone. Archive the session first if you want to keep this run.
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={onCancelReset} className={ui.btnText}>Cancel</button>
            <button onClick={onResetCase} className={ui.btnDanger}>
              <RotateCcw className="w-4 h-4" />Reset case
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
